import React, { useCallback, useMemo, useRef, useState } from "react";

import moment from "moment";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";

import useURLSearchState, { transforms } from "Hooks/useURLSearchState";
import ActivityLogModal from "src/organization/pages/project/environment/metrics/components/ActivityLogModal";
import LastUpdated from "src/organization/pages/project/environment/metrics/components/LastUpdated";
import Timeframe from "src/organization/pages/project/environment/metrics/components/TimeframeSelector/TimeframeSelector";
import ToggleActivitiesOverlay from "src/organization/pages/project/environment/metrics/components/ToggleActivitiesOverlay";
import useDedicatedWorker from "src/organization/pages/project/environment/metrics/hooks/useDedicatedWorker";
import useMultiChart from "src/organization/pages/project/environment/metrics/hooks/useMultiChart";
import useRange from "src/organization/pages/project/environment/metrics/hooks/useRange";
import useStickyTimecontrol from "src/organization/pages/project/environment/metrics/hooks/useStickyTimecontrol";
import {
  DG2_QUERY,
  timeframes
} from "src/organization/pages/project/environment/metrics/resources/settings";
import * as V from "src/organization/pages/project/environment/metrics/resources/views.styles";

import Cpu from "../Cpu";
import Disk from "../Disk";
import Memory from "../Memory";

import * as S from "./Split.styles";

// user zoom must span at least this range (in seconds) to ensure at least 3 data points display
const minimumRange = 180;

const Split = ({ link }) => {
  const [maxZoom, setMaxZoom] = useState(false);
  const { selectedTimeframe, setSelectedTimeframe } = useRange(
    timeframes,
    minimumRange
  );

  const range = useMemo(() => {
    if (selectedTimeframe.range) {
      return selectedTimeframe.range;
    }

    return { from: selectedTimeframe.from, to: selectedTimeframe.to };
  }, [JSON.stringify(selectedTimeframe)]);

  const activities = useDedicatedWorker(selectedTimeframe);

  const { result: web, isLoading: isLoadingWeb } = useMultiChart(
    DG2_QUERY,
    link.collection,
    link.href,
    selectedTimeframe.interval,
    range,
    { filters: [{ key: "hostType", value: "web" }] }
  );

  const [webHosts, setWebHosts] = useURLSearchState(
    web.hosts || [],
    "webHosts",
    transforms.array
  );

  const selectedWebHosts = useMemo(() => {
    return webHosts.map(host =>
      web.hosts.find(fullHost => fullHost?.startsWith(host))
    );
  }, [webHosts]);

  const { result: unified, isLoading: isLoadingUnified } = useMultiChart(
    DG2_QUERY,
    link.collection,
    link.href,
    selectedTimeframe.interval,
    range,
    { filters: [{ key: "hostType", value: "unified" }] }
  );

  const [unifiedHosts, setUnifiedHosts] = useURLSearchState(
    unified.hosts || [],
    "unifiedHosts",
    transforms.array
  );

  const selectedUnifiedHosts = useMemo(() => {
    return unifiedHosts.map(host =>
      unified.hosts.find(fullHost => fullHost?.startsWith(host))
    );
  }, [unifiedHosts]);

  const minimumInterval = Math.max(selectedTimeframe?.interval || 0, 60);

  const onBrush = useCallback(
    (from, to) => {
      if (maxZoom) {
        setSelectedTimeframe({
          from: selectedTimeframe.from,
          to: selectedTimeframe.to
        });
        return;
      }
      const selectedRange = moment(to).diff(moment(from), "minutes");
      if (selectedRange < 3) {
        const newEndpoint = moment(from).add(3, "minutes").format();
        setMaxZoom(true);
        setSelectedTimeframe({ from, to: newEndpoint });
      } else {
        setSelectedTimeframe({ from, to });
      }
    },
    [maxZoom, setMaxZoom, setSelectedTimeframe]
  );

  const headerRef = useRef(null);
  useStickyTimecontrol({ ref: headerRef });

  const onTimeframeChange = useCallback(
    timeframe => {
      setMaxZoom(false);
      setSelectedTimeframe(timeframe);
    },
    [setMaxZoom, setSelectedTimeframe]
  );
  const lastDataPoint = web?.cpu?.last.timestamp;
  const firstDataPoint = web?.cpu?.data[0] && web.cpu.data[0].timestamp;

  return (
    <>
      <ActivityLogModal />
      <V.Header ref={headerRef}>
        <V.Heading tag="h2">
          <FormattedMessage id="metrics.split.heading" />
        </V.Heading>
        <V.TimeframeColumn>
          {selectedTimeframe && (
            <LastUpdated
              interval={minimumInterval}
              lastDataPoint={lastDataPoint}
              isCustomTimeframe={!selectedTimeframe.id}
            />
          )}
          <V.Row style={{ marginLeft: "auto" }}>
            <ToggleActivitiesOverlay />
            <Timeframe
              selectedTimeframe={selectedTimeframe}
              timeframes={timeframes}
              onChange={onTimeframeChange}
              to={lastDataPoint}
              from={firstDataPoint}
            />
          </V.Row>
        </V.TimeframeColumn>
      </V.Header>
      <S.Layout>
        <S.Column>
          <S.HostHeader>
            <S.HostType>
              <FormattedMessage id="metrics.split.web" />{" "}
              <S.HostCount>({web.hosts.length - 1})</S.HostCount>
            </S.HostType>
            <S.HostSelector
              hosts={web.hosts}
              selected={webHosts}
              onChange={e => setWebHosts(e)}
            />
          </S.HostHeader>
          <S.ChartContainer>
            <Cpu
              hosts={selectedWebHosts}
              data={web.cpu}
              isLoading={isLoadingWeb}
              timeframe={selectedTimeframe}
              onBrush={onBrush}
              maxZoom={maxZoom}
              activities={activities}
            />
            <Memory
              hosts={selectedWebHosts}
              data={web.memory}
              isLoading={isLoadingWeb}
              timeframe={selectedTimeframe}
              onBrush={onBrush}
              maxZoom={maxZoom}
              activities={activities}
            />
            {web.mountpoint.map(
              mountpoint =>
                web.disks &&
                web.disks[mountpoint] && (
                  <Disk
                    diskPerRow={1}
                    key={mountpoint}
                    hosts={selectedWebHosts}
                    mountpoint={mountpoint}
                    isLoading={isLoadingWeb}
                    data={web.disks[mountpoint]}
                    timeframe={selectedTimeframe}
                    onBrush={onBrush}
                    maxZoom={maxZoom}
                    activities={activities}
                  />
                )
            )}
          </S.ChartContainer>
        </S.Column>
        <S.Column>
          <S.HostHeader>
            <S.HostType>
              <FormattedMessage id="metrics.split.unified" />{" "}
              <S.HostCount>({unified.hosts.length - 1})</S.HostCount>
            </S.HostType>
            <S.HostSelector
              hosts={unified.hosts}
              selected={unifiedHosts}
              onChange={e => setUnifiedHosts(e)}
            />
          </S.HostHeader>
          <S.ChartContainer>
            <Cpu
              hosts={selectedUnifiedHosts}
              data={unified.cpu}
              isLoading={isLoadingUnified}
              timeframe={selectedTimeframe}
              onBrush={onBrush}
              maxZoom={maxZoom}
              activities={activities}
            />
            <Memory
              hosts={selectedUnifiedHosts}
              data={unified.memory}
              isLoading={isLoadingUnified}
              timeframe={selectedTimeframe}
              onBrush={onBrush}
              maxZoom={maxZoom}
              activities={activities}
            />
            {unified.mountpoint.map(
              mountpoint =>
                unified.disks &&
                unified.disks[mountpoint] && (
                  <Disk
                    diskPerRow={1}
                    key={mountpoint}
                    hosts={selectedUnifiedHosts}
                    mountpoint={mountpoint}
                    isLoading={isLoadingUnified}
                    data={unified.disks[mountpoint]}
                    timeframe={selectedTimeframe}
                    onBrush={onBrush}
                    maxZoom={maxZoom}
                    activities={activities}
                  />
                )
            )}
          </S.ChartContainer>
        </S.Column>
      </S.Layout>
    </>
  );
};

Split.propTypes = {
  link: PropTypes.shape({
    collection: PropTypes.string,
    href: PropTypes.string
  }),
  dimensions: PropTypes.array
};

export default Split;
